import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Trophy, Plus, Search, UserCheck, Compass, Award, ArrowLeft, Zap, Sparkles } from 'lucide-react';
import QuickTournamentModal from '../components/QuickTournamentModal';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { tournamentAPI } from '../services/tournamentApi';
import { sportTypeAPI } from '../../services/api';
import TournamentCard from '../components/TournamentCard';
import { btn } from '../../utils/buttonStyles';
import ShareModal from '../../components/common/ShareModal';
import EmptyState from '../components/EmptyState';
import { transformTournamentData } from '../utils/tournamentHelpers';
import Tabs from '../../components/common/Tabs';

export default function TournamentPage() {
  const navigate = useNavigate();
  const { squadId } = useParams();           // set on /squads/:squadId/tournaments
  const { state: navState } = useLocation(); // supplementary display data (name, sportTypeId)
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { smartSchedule: smartScheduleEnabled } = useFeatureFlags();

  // Squad/group context — driven by URL param, supplemented by location state
  const isSquadContext = !!squadId;
  const squadOwnerId = squadId || null;
  const squadOwnerType = squadId ? 'group' : null;
  const squadOwnerName = navState?.ownerName || null;

  const [activeTab, setActiveTab] = useState('my-tournaments');
  const [searchTerm, setSearchTerm] = useState('');
  const [myTournaments, setMyTournaments] = useState([]);
  const [availableTournaments, setAvailableTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareModal, setShareModal] = useState({ isOpen: false, tournament: null });
  const [quickModalOpen, setQuickModalOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sportTypeId, setSportTypeId] = useState('');
  const [sportTypes, setSportTypes] = useState([]);

  // Fetch sport types once for the filter dropdown (cached — see sportTypeAPI.getAll)
  useEffect(() => {
    sportTypeAPI.getAll().then(data => {
      if (Array.isArray(data)) setSportTypes(data);
    }).catch(() => {});
  }, []);

  // Set breadcrumb
  useEffect(() => {
    if (isSquadContext) {
      setBreadcrumbItems([
        { label: 'Home', path: '/' },
        { label: squadOwnerName || 'Squad', path: `/squads/${squadId}` },
        { label: 'Tournaments' }
      ]);
    } else {
      setBreadcrumbItems([
        { label: 'Home', path: '/' },
        { label: 'Tournaments' }
      ]);
    }
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems, isSquadContext, squadOwnerId, squadOwnerName]);

  const fetchTournaments = useCallback(async () => {
    setLoading(true);
    setError(null);

    const filters = {
      excludeCompleted: !showCompleted,
      ...(sportTypeId ? { sportTypeId } : {})
    };

    try {
      if (isSquadContext) {
        // Squad mode: fetch only tournaments owned by this group
        const response = await tournamentAPI.getAll({
          ...filters,
          ownerType: squadOwnerType,
          ownerId: squadOwnerId,
        });
        setMyTournaments((response?.data || []).map(transformTournamentData));
        setAvailableTournaments([]);
      } else {
        const [allResponse, myResponse] = await Promise.all([
          tournamentAPI.getAll(filters),
          isAuthenticated && user?.id
            ? tournamentAPI.getMyTournaments(user.id, filters)
            : Promise.resolve(null)
        ]);

        const allData = allResponse?.data || [];
        // Browse tab: exclude tournaments the current user organized
        const browseData = allData.filter(t => !user?.id || t.organizerUserId !== user.id);
        setAvailableTournaments(browseData.map(transformTournamentData));

        const myData = myResponse?.data || [];
        setMyTournaments(myData.map(transformTournamentData));
      }
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
      setError('Failed to load tournaments. Please try again later.');
      setAvailableTournaments([]);
      setMyTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, showCompleted, sportTypeId, isSquadContext, squadOwnerType, squadOwnerId]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  // Search is the only client-side filter (instant UX, no round-trip needed)
  const filterBySearch = (list) =>
    searchTerm
      ? list.filter(t =>
          t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.sport.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : list;

  const handleJoinTournament = (tournamentId) => {
    toast.success('Joined tournament! Registration confirmation will be sent to your email.');
  };

  const handleCreateTournament = () => {
    if (isSquadContext) {
      navigate('/create-tournament', {
        state: {
          ownerType: 'group',
          ownerId: squadId,
          ownerName: squadOwnerName,
          sportTypeId: navState?.sportTypeId,
        },
      });
    } else {
      navigate('/create-tournament');
    }
  };

  const handleTournamentClick = (tournament) => {
    navigate(`/tournament/${tournament.id}?tab=details`, {
      state: {
        tournament,
        ...(isSquadContext && {
          fromSquad: { id: squadId, name: squadOwnerName || 'Squad' }
        })
      }
    });
  };

  const handleShareClick = (tournament) => {
    setShareModal({ isOpen: true, tournament });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className={`mt-2 ${theme.textSecondary}`}>Loading tournaments...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredBrowse = filterBySearch(availableTournaments);
  const filteredMy = filterBySearch(myTournaments);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div className="flex items-start gap-x-3">
            {isSquadContext && (
              <button
                onClick={() => navigate(`/squads/${squadId}`)}
                className={`mt-1 p-2 ${theme.cardHover} rounded-lg transition-colors flex-shrink-0`}
                title={`Back to ${squadOwnerName || 'squad'}`}
              >
                <ArrowLeft className={`w-5 h-5 ${theme.textSecondary}`} />
              </button>
            )}
            <div>
              <h1 className={`text-3xl font-bold ${theme.text} mb-2`}>
                {isSquadContext ? `${squadOwnerName || 'Squad'} Tournaments` : 'Tournaments & Leagues'}
              </h1>
              <p className={`${theme.textSecondary}`}>
                {isSquadContext
                  ? `Tournaments organized by ${squadOwnerName || 'this squad'}`
                  : 'Compete, connect, and conquer in sports tournaments'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 sm:mt-0">
            {smartScheduleEnabled && (
              <button
                onClick={() => navigate('/tournaments/explore-formats')}
                className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex items-center`}
                title="Try any schedule format on mock data — no tournament required"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Explore Formats
              </button>
            )}
            <button
              onClick={() => setQuickModalOpen(true)}
              className={`${btn.base} ${btn.md} ${theme.buttonSecondary} flex items-center`}
            >
              <Zap className="w-4 h-4 mr-2" />
              Quick Tournament
            </button>
            <button
              onClick={handleCreateTournament}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Tournament
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {!isSquadContext && (
          <Tabs
            tabs={[
              { id: 'my-tournaments', label: 'My Tournaments', shortLabel: 'My', icon: UserCheck },
              { id: 'browse', label: 'Browse', icon: Compass },
              { id: 'leagues', label: 'Leagues', icon: Award }
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showMobileMenu={false}
            className="mb-6"
          />
        )}

        {/* Search and Filters */}
        {activeTab !== 'leagues' && (
          <div className="space-y-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tournaments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border ${theme.border} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
                />
              </div>
              {/* Sport type filter */}
              <select
                value={sportTypeId}
                onChange={(e) => setSportTypeId(e.target.value)}
                className={`px-4 py-3 border ${theme.border} rounded-xl ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              >
                <option value="">All Sports</option>
                {sportTypes.map(s => (
                  <option key={s.id} value={s.id}>{s.sportName}</option>
                ))}
              </select>
            </div>

            {/* Show Completed Checkbox */}
            <label className="flex items-center cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className={`ml-2 text-sm ${theme.text}`}>Show completed tournaments</span>
            </label>
          </div>
        )}

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          filteredBrowse.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBrowse.map(tournament => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  isMyTournament={false}
                  onView={handleTournamentClick}
                  onShare={handleShareClick}
                  onJoin={handleJoinTournament}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Trophy}
              title="No tournaments found"
              description={searchTerm ? `No tournaments match "${searchTerm}".` : 'No tournaments available at the moment.'}
              buttonText={!searchTerm ? 'Create Your First Tournament' : undefined}
              onButtonClick={!searchTerm ? handleCreateTournament : undefined}
            />
          )
        )}

        {/* My Tournaments Tab — also used as the only view in squad context */}
        {(activeTab === 'my-tournaments' || isSquadContext) && (
          isAuthenticated || isSquadContext ? (
            filteredMy.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMy.map(tournament => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                    isMyTournament={true}
                    onView={handleTournamentClick}
                    onShare={handleShareClick}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Trophy}
                title={searchTerm ? 'No matching tournaments' : (isSquadContext ? 'No tournaments yet' : 'No tournaments yet')}
                description={
                  searchTerm
                    ? `No tournaments match "${searchTerm}".`
                    : isSquadContext
                      ? `${squadOwnerName || 'This squad'} hasn't created any tournaments yet.`
                      : 'Create your first tournament to start organizing!'
                }
                buttonText={!searchTerm ? 'Create Tournament' : undefined}
                onButtonClick={!searchTerm ? handleCreateTournament : undefined}
              />
            )
          ) : (
            <EmptyState
              icon={Trophy}
              title="Sign in to view your tournaments"
              description="Track your progress and manage your tournament organization"
            />
          )
        )}

        {activeTab === 'leagues' && (
          <EmptyState
            icon={Trophy}
            title="Leagues Coming Soon"
            description="Join ongoing leagues and seasonal competitions"
          />
        )}

        <ShareModal
          isOpen={shareModal.isOpen}
          onClose={() => setShareModal({ isOpen: false, tournament: null })}
          entity={{
            id: shareModal.tournament?.id,
            name: shareModal.tournament?.name,
            description: shareModal.tournament?.description
          }}
          entityType="tournament"
          urlPattern="/tournament/{id}"
          currentUser={user}
        />

        <QuickTournamentModal
          isOpen={quickModalOpen}
          onClose={() => setQuickModalOpen(false)}
          squadId={squadId || null}
          initialSportTypeId={navState?.sportTypeId || null}
        />
      </div>
    </div>
  );
}
